import type { Board } from "../engine/board";
import type { Direction } from "../engine/direction";
import { isTintable } from "../engine/interfaces";
import type { PhysicalThing } from "../engine/physicalThing";
import { Player } from "../engine/player";
import type { Thing } from "../engine/thing";
import type { AnimationLoader } from "./animationLoader";
import type { Sprite } from "./sprites/sprite";
import { ManagedAnimation } from "./sprites/managedAnimation";
import { getCurrentDirection, getCurrentState } from "./drawableState";

export class DrawableManager {
	private managedThings: Map<Thing, ManagedAnimation> = new Map();
	private lastKnownDirection: Map<Thing, Direction | null> = new Map();
	private lastKnownState: Map<Thing, string | null> = new Map();
	private animationLoader: AnimationLoader;
	private playerReloadPending: boolean = false;
	private generation: number = 0;

	constructor(animationLoader: AnimationLoader) {
		this.animationLoader = animationLoader;
	}

	async addThing(thing: PhysicalThing): Promise<boolean> {
		const animation = await this.animationLoader.getAnimation(thing);
		if (animation === null) return false;
		this.managedThings.set(thing, new ManagedAnimation(animation));
		this.lastKnownDirection.set(thing, getCurrentDirection(thing));
		this.lastKnownState.set(thing, this.getTrackedState(thing));
		return true;
	}

	// The Player's composed sprite has no SomethingWithState; its visual-state key
	// stands in so key changes trigger the same reload as state changes.
	private getTrackedState(thing: PhysicalThing): string | null {
		const provider = this.animationLoader.getPlayerVisualStateProvider();
		if (thing instanceof Player && provider) return provider.getStateKey();
		return getCurrentState(thing);
	}

	// The walking state flips mid-tween, between the per-turn syncWithBoard calls, so the
	// player's state key is re-checked every rendered frame. Swaps in place once loaded —
	// deleting first would blank the player until the async reload resolves.
	refreshPlayerIfStateChanged(player: Player): void {
		if (this.playerReloadPending || !this.managedThings.has(player)) return;
		const capturedState = this.getTrackedState(player);
		if (capturedState === (this.lastKnownState.get(player) ?? null)) return;
		const capturedDirection = getCurrentDirection(player);
		const capturedGeneration = this.generation;
		this.playerReloadPending = true;
		this.animationLoader
			.getAnimation(player)
			.then((animation) => {
				// Install only if the captured inputs are still current; a skipped install
				// re-triggers next frame via the key mismatch, with fresh inputs.
				const stillCurrent =
					animation !== null &&
					this.generation === capturedGeneration &&
					this.managedThings.has(player) &&
					this.getTrackedState(player) === capturedState &&
					getCurrentDirection(player) === capturedDirection;
				if (!stillCurrent) return;
				this.managedThings.set(player, new ManagedAnimation(animation));
				this.lastKnownDirection.set(player, capturedDirection);
				this.lastKnownState.set(player, capturedState);
			})
			.catch((err) => {
				console.error("Player sprite reload failed:", err);
				// Stamping the key stops the per-frame retry; the previous animation
				// stays until the next state change.
				this.lastKnownState.set(player, capturedState);
			})
			.finally(() => {
				this.playerReloadPending = false;
			});
	}

	removeThing(thing: Thing): void {
		this.managedThings.delete(thing);
		this.lastKnownDirection.delete(thing);
		this.lastKnownState.delete(thing);
	}

	passTime(ms: number): void {
		for (const [thing, managedAnim] of this.managedThings) {
			let effectiveMs = ms;
			if (isTintable(thing)) {
				effectiveMs *= thing.getAnimationSpeedMultiplier();
			}
			if (!managedAnim.isExpired()) {
				managedAnim.passTime(effectiveMs);
			}
		}
	}

	getSprite(thing: Thing): Sprite | null {
		const managedAnim = this.managedThings.get(thing);
		if (!managedAnim || managedAnim.isExpired()) return null;
		return managedAnim.getCurrentSprite();
	}

	containsThing(thing: Thing): boolean {
		return this.managedThings.has(thing);
	}

	async syncWithBoard(board: Board, removeStale: boolean = true): Promise<void> {
		const boardThings = new Set<PhysicalThing>(board.getPhysicalThings());

		// removeStale=false during the pre-effects sync keeps sprites of things removed
		// this turn (e.g. a dead mob) alive through the pre-effect window.
		if (removeStale) {
			for (const thing of this.managedThings.keys()) {
				if (!boardThings.has(thing as PhysicalThing)) {
					this.managedThings.delete(thing);
					this.lastKnownDirection.delete(thing);
					this.lastKnownState.delete(thing);
				}
			}
		}

		// Check for direction/state changes on existing things and add new things
		const reloads: Promise<void>[] = [];
		for (const thing of boardThings) {
			if (!thing.isVisible()) continue;
			if (this.managedThings.has(thing)) {
				const currentDir = getCurrentDirection(thing);
				const lastDir = this.lastKnownDirection.get(thing) ?? null;
				const currentState = this.getTrackedState(thing);
				const lastState = this.lastKnownState.get(thing) ?? null;
				if (currentDir !== lastDir || currentState !== lastState) {
					this.managedThings.delete(thing);
					this.lastKnownDirection.delete(thing);
					this.lastKnownState.delete(thing);
					reloads.push(this.addThing(thing).then(() => {}));
				}
			} else {
				reloads.push(this.addThing(thing).then(() => {}));
			}
		}
		await Promise.all(reloads);
	}

	clear(): void {
		this.generation++;
		this.managedThings.clear();
		this.lastKnownDirection.clear();
		this.lastKnownState.clear();
	}
}
